"use client"

import { useProjectMembershipPrompt } from "@/hooks/useProjectMembershipPrompt"
import { useMemo, useRef, useState, useEffect, useCallback, type Dispatch, type SetStateAction } from "react"
import { addDays, format, differenceInCalendarDays, isWeekend } from "date-fns"
import {
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Crosshair,
  Link2,
  Layers,
  GripVertical,
  Loader2,
  Check,
  AlertTriangle,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { Checkbox } from "@/components/ui/checkbox"
import type { GanttTask, GanttDependency } from "../gantt-chart/data/types"
import type { UseGanttData } from "./useGanttData"
import { useCompanyMembers } from "./useCompanyMembers"
import { useEntityComments } from "@/hooks/useEntityComments"
import { uploadScheduleTaskAttachments, deleteScheduleTaskAttachment, fetchScheduleTaskAttachments, type ScheduleFileAttachment } from "./schedule-attachments"
import { GanttWeatherBadge } from "./GanttWeatherBadge"
import {
  toDate,
  toDateStr,
  daysBetween,
  STATUS_LABEL,
  STATUS_DOT_COLOR,
  DEP_TYPE_LABEL,
  getScheduleHealthFlag,
  SCHEDULE_HEALTH_LABEL,
} from "./gantt-utils"
import { StandardButton, SaveChangesButton, DeleteRecordButton, tertiaryHover } from "@/styles/StandardButtons"
import { GenericCommentsSection, TEAM_CLIENT_VENDOR_AUDIENCES } from "@/styles/GenericCommentsSection"
import { GenericAttachmentsSection } from "@/styles/GenericAttachmentsSection"
import { AddTaskButton } from "./AddTaskButton"
import { colors } from "@/design-system/colors"
import "@/design-system/forms.css"
import { addWorkingDays, snapToNearestWorkingDay, DEFAULT_WORKING_DAYS, type WorkingDaysConfig } from "../schedule-date-engine"
import { FloatingAlert } from "@/components/ui/floating-alert"
import { Textarea } from '@/components/ui/textarea';

/**
 * Gantt -- real Supabase-backed Gantt (phase97/phase98, 2026-09-29).
 * Data + mutations live in useGanttData (shared with GanttListView.tsx,
 * instantiated once in scheduling-page.tsx) -- this component is purely
 * the timeline presentation + its own UI-only state (selection, zoom,
 * drag, collapse, lookahead). Select mode / checked ids are lifted to
 * scheduling-page.tsx so its CardHeader action bar (Add Phase/Add Task/
 * Select/bulk actions) can drive the same selection these rows render
 * checkboxes for -- see that file's header comment.
 */

function toDateStrLocal(d: Date): string {
  return toDateStr(d)
}

type Zoom = "day" | "week" | "month" | "quarter"

const ROW_HEIGHT = 40
const HEADER_HEIGHT = 52
const LIST_WIDTH = 280
// Each zoom level's pxPerDay is computed from the ACTUAL visible viewport
// width, not a fixed constant -- otherwise "Week"/"Month" just shrink the
// same total date range down to a sliver, leaving most of the Gantt
// blank (the bug this replaces). "Day" stays a fixed comfortable width
// (already worked well); Week/Month/Quarter instead target "make
// roughly N days fill the visible viewport", exactly like a calendar
// app's Day/Week/Month view -- you scroll for more, but what's on
// screen right now IS about a week/month/quarter. A floor keeps degenerate
// (very narrow) viewports from collapsing to unreadable slivers.
const DAY_ZOOM_PX = 34
const ZOOM_TARGET_DAYS: Record<Exclude<Zoom, "day">, number> = { week: 7, month: 30, quarter: 90 }
const ZOOM_MIN_PX: Record<Exclude<Zoom, "day">, number> = { week: 16, month: 6, quarter: 2.5 }
const TODAY = new Date()
const TODAY_STR = format(TODAY, "yyyy-MM-dd")
const LOOKAHEAD_DAYS = 42
const LOOKAHEAD_LEAD_DAYS = 4
// Extra room to plan before/after the project's own task dates (spec #3):
// the timeline never ends exactly at the earliest/latest task, there's
// always about a month of blank runway on each side to drag a new task
// into or extend an existing one.
const RANGE_PADDING_DAYS = 30

type Selection = { type: "task"; id: string } | { type: "phase"; id: string } | null

type Row =
  | { kind: "phase"; task: GanttTask; count: number }
  | { kind: "task"; task: GanttTask; phaseId: string }

interface DragState {
  ids: string[]
  mode: "move" | "resize"
  startX: number
  orig: Map<string, { start: Date; end: Date }>
  // Written by onMouseMove on every move, read by onMouseUp on drop. Lives
  // on the ref (not React state) on purpose: the mousemove/mouseup
  // listeners are registered ONCE via window.addEventListener at drag
  // start, so the specific closure instance that ends up attached is
  // whichever one existed at that moment -- reading `dragPreview` state
  // from inside it would always see that moment's value (null, since no
  // drag was active yet), never the value it was updated to during the
  // drag. dragRef.current is the same mutable ref object across every
  // render, so writes made during the drag are visible no matter which
  // stale closure ends up reading it.
  preview?: { startDate: string; endDate: string }
}

interface DragPreview {
  taskId: string
  startDate: string
  endDate: string
}

interface RowDragState {
  kind: "phase" | "task"
  id: string
}

export function Gantt({
  projectId,
  companyId,
  userId,
  ganttData,
  selectMode,
  checkedIds,
  setCheckedIds,
  search = "",
  workingDays = DEFAULT_WORKING_DAYS,
}: {
  projectId?: string
  companyId?: string | null
  userId?: string
  ganttData: UseGanttData
  selectMode: boolean
  checkedIds: Set<string>
  setCheckedIds: Dispatch<SetStateAction<Set<string>>>
  search?: string
  workingDays?: WorkingDaysConfig
}) {
  const {
    tasks,
    dependencies,
    loading,
    tasksById,
    phases,
    childrenByPhase,
    phaseSummaries,
    phaseColor,
    updateTask,
    addPhase,
    addTask,
    deletePhase,
    renamePhase,
    reorderSiblings,
    bulkShiftDates,
  } = ganttData

  const [selection, setSelection] = useState<Selection>(null)
  const [collapsedPhases, setCollapsedPhases] = useState<Set<string>>(new Set())
  const [zoom, setZoom] = useState<Zoom>("day")
  const [lookahead, setLookahead] = useState(false)
  const [viewportWidth, setViewportWidth] = useState(0)
  // Ghost/preview bar (spec #9-#12) -- only ever set for the SINGLE task
  // being dragged (never for a multi-select bulk drag, per spec #11), and
  // always computed with the exact same working-days/snap rules the real
  // drop will use, so what you see is what you get.
  const [dragPreview, setDragPreview] = useState<DragPreview | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<DragState | null>(null)
  const didDragRef = useRef(false)
  const rowDragRef = useRef<RowDragState | null>(null)

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width
      if (width) setViewportWidth(width)
    })
    observer.observe(el)
    return () => observer.disconnect()
    // Re-attach once the scrollable container actually mounts (it doesn't
    // exist yet while the empty-state placeholder is showing).
  }, [phases.length > 0])

  // Fixed relative to TODAY (spec #7) -- never derived from task/project
  // dates, so it's always exactly "today - 4 days" through "today + 42
  // days" (6 weeks), not whatever happens to overlap the visible tasks.
  const lookaheadStartDate = useMemo(() => addDays(TODAY, -LOOKAHEAD_LEAD_DAYS), [])
  const lookaheadEndDate = useMemo(() => addDays(TODAY, LOOKAHEAD_DAYS), [])
  const lookaheadStart = useMemo(() => toDateStrLocal(lookaheadStartDate), [lookaheadStartDate])
  const lookaheadEnd = useMemo(() => toDateStrLocal(lookaheadEndDate), [lookaheadEndDate])
  const isTaskInLookahead = useCallback(
    (task: GanttTask) => !lookahead || (task.startDate <= lookaheadEnd && task.endDate >= lookaheadStart),
    [lookahead, lookaheadEnd, lookaheadStart]
  )

  const { min: rangeStart, max: rangeEnd } = useMemo(() => {
    // Lookahead: the visible window IS the fixed today-4..today+42 range,
    // regardless of which task dates happen to fall inside it.
    if (lookahead) return { min: lookaheadStartDate, max: lookaheadEndDate }
    // Otherwise: the project's own task range, plus ~a month of extra
    // runway on each side to plan into (spec #3) -- never ends exactly at
    // the earliest/latest task date.
    if (tasks.length === 0) return { min: addDays(TODAY, -RANGE_PADDING_DAYS), max: addDays(TODAY, RANGE_PADDING_DAYS) }
    const starts = tasks.map((t) => toDate(t.startDate))
    const ends = tasks.map((t) => toDate(t.endDate))
    return {
      min: addDays(starts.reduce((a, b) => (a < b ? a : b)), -RANGE_PADDING_DAYS),
      max: addDays(ends.reduce((a, b) => (a > b ? a : b)), RANGE_PADDING_DAYS),
    }
  }, [tasks, lookahead, lookaheadStartDate, lookaheadEndDate])

  const totalDays = daysBetween(rangeStart, rangeEnd) + 1
  const availableWidth = Math.max(0, viewportWidth - LIST_WIDTH)
  const pxPerDay = zoom === "day" ? DAY_ZOOM_PX : Math.max(ZOOM_MIN_PX[zoom], availableWidth / ZOOM_TARGET_DAYS[zoom])
  // Never render narrower than the visible viewport -- a short date range
  // (or a low pxPerDay at Month/Quarter) would otherwise leave the timeline
  // shorter than the screen, showing blank space instead of more time.
  const timelineWidth = Math.max(totalDays * pxPerDay, availableWidth)
  const xForDateStr = useCallback((dateStr: string) => daysBetween(rangeStart, toDate(dateStr)) * pxPerDay, [rangeStart, pxPerDay])

  const searchTerm = search.trim().toLowerCase()
  const rows = useMemo<Row[]>(() => {
    const out: Row[] = []
    for (const phase of phases) {
      const phaseMatches = !searchTerm || phase.name.toLowerCase().includes(searchTerm)
      let children = childrenByPhase.get(phase.id) || []
      if (lookahead) children = children.filter(isTaskInLookahead)
      const visibleChildren = phaseMatches || !searchTerm ? children : children.filter((t) => t.name.toLowerCase().includes(searchTerm))
      if (searchTerm && !phaseMatches && visibleChildren.length === 0) continue
      if (lookahead && !searchTerm && visibleChildren.length === 0) continue
      const summary = phaseSummaries.get(phase.id)
      out.push({ kind: "phase", task: phase, count: lookahead || searchTerm ? visibleChildren.length : summary?.count ?? 0 })
      if (collapsedPhases.has(phase.id)) continue
      for (const task of visibleChildren) out.push({ kind: "task", task, phaseId: phase.id })
    }
    return out
  }, [phases, phaseSummaries, childrenByPhase, collapsedPhases, lookahead, isTaskInLookahead, searchTerm])

  const rowIndexByTaskId = useMemo(() => {
    const m = new Map<string, number>()
    rows.forEach((r, i) => m.set(r.task.id, i))
    return m
  }, [rows])

  const selectedTask = selection?.type === "task" ? tasksById.get(selection.id) ?? null : null
  const selectedPhase = selection?.type === "phase" ? tasksById.get(selection.id) ?? null : null

  const taskDeps = useMemo(() => {
    if (!selectedTask) return { incoming: [] as GanttDependency[], outgoingIds: [] as string[] }
    return {
      incoming: dependencies.filter((d) => d.toTaskId === selectedTask.id),
      outgoingIds: dependencies.filter((d) => d.fromTaskId === selectedTask.id).map((d) => d.toTaskId),
    }
  }, [selectedTask, dependencies])

  const relatedIds = useMemo(() => {
    if (!selectedTask) return null
    return new Set([selectedTask.id, ...taskDeps.incoming.map((d) => d.fromTaskId), ...taskDeps.outgoingIds])
  }, [selectedTask, taskDeps])

  const monthTicks = useMemo(() => {
    const out: { label: string; left: number; width: number }[] = []
    let cursor = 0
    while (cursor < totalDays) {
      const date = addDays(rangeStart, cursor)
      const monthLabel = format(date, "MMMM yyyy")
      let span = 0
      while (cursor + span < totalDays && format(addDays(rangeStart, cursor + span), "MMMM yyyy") === monthLabel) span++
      out.push({ label: monthLabel, left: cursor * pxPerDay, width: span * pxPerDay })
      cursor += span
    }
    return out
  }, [rangeStart, totalDays, pxPerDay])

  const dayTicks = useMemo(() => {
    const out: { date: Date; left: number; weekend: boolean }[] = []
    for (let i = 0; i < totalDays; i++) {
      const date = addDays(rangeStart, i)
      out.push({ date, left: i * pxPerDay, weekend: isWeekend(date) })
    }
    return out
  }, [rangeStart, totalDays, pxPerDay])

  // Day numbers/weekday letters stay visible at every zoom (spec #4) -- at
  // Quarter (or a narrow viewport) pxPerDay can get too small to fit a
  // label on every single day, so only every Nth day shows text; every
  // day still gets its tick/gridline regardless.
  const dayLabelStep = pxPerDay >= 16 ? 1 : pxPerDay >= 8 ? 2 : pxPerDay >= 4 ? 5 : 10

  // ---- drag / resize (single or multi-select) ---------------------------------
  // Click-vs-drag (spec #4): mousedown on a bar never opens the drawer by
  // itself -- it only arms a potential drag. didDragRef flips true the
  // moment any real movement happens; the timeline row's onClick handler
  // (fired AFTER mouseup, from the browser's normal click sequence) checks
  // that flag and skips opening the drawer when a real drag/resize just
  // happened. A plain click with zero movement never sets the flag, so the
  // click goes through and opens the drawer normally.
  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      const ds = dragRef.current
      if (!ds) return
      const deltaDays = Math.round((e.clientX - ds.startX) / pxPerDay)
      if (deltaDays !== 0) didDragRef.current = true
      ;(window as any).__ganttDragDelta = deltaDays

      // Ghost/preview (spec #9-#12): only for a single-task drag, computed
      // with the SAME working-days + snap rules the drop will use.
      if (ds.ids.length === 1) {
        const id = ds.ids[0]
        const task = tasksById.get(id)
        const orig = ds.orig.get(id)
        if (task && orig) {
          let newStart: Date
          let newEnd: Date
          if (ds.mode === "move") {
            newStart = snapToNearestWorkingDay(addDays(orig.start, deltaDays), workingDays)
            newEnd = addWorkingDays(newStart, task.duration, workingDays)
          } else {
            newStart = orig.start
            const candidateEnd = addDays(orig.end, deltaDays)
            const snappedEnd = snapToNearestWorkingDay(candidateEnd, workingDays)
            newEnd = snappedEnd < newStart ? newStart : snappedEnd
          }
          const startStr = toDateStrLocal(newStart)
          const endStr = toDateStrLocal(newEnd)
          // Written to the ref (see DragState.preview) so the drop -- which
          // fires on whichever stale onMouseUp closure is attached -- still
          // reads the up-to-date value instead of the null it was created
          // with.
          ds.preview = { startDate: startStr, endDate: endStr }
          setDragPreview((prev) => (prev && prev.taskId === id && prev.startDate === startStr && prev.endDate === endStr ? prev : { taskId: id, startDate: startStr, endDate: endStr }))
        }
      }
    },
    [pxPerDay, tasksById, workingDays]
  )

  const onMouseUp = useCallback(() => {
    const ds = dragRef.current
    dragRef.current = null
    window.removeEventListener("mousemove", onMouseMove)
    window.removeEventListener("mouseup", onMouseUp)
    const deltaDays = (window as any).__ganttDragDelta ?? 0
    ;(window as any).__ganttDragDelta = 0
    setDragPreview(null)
    if (!ds || deltaDays === 0) return

    if (ds.mode === "move" && ds.ids.length > 1) {
      bulkShiftDates(ds.ids, deltaDays)
      return
    }
    const id = ds.ids[0]
    // Apply EXACTLY what the ghost showed -- read from the ref (ds.preview),
    // never React state, so the drop always lands where the preview said it
    // would regardless of which stale closure this listener call is.
    if (ds.preview) {
      updateTask(id, { startDate: ds.preview.startDate, endDate: ds.preview.endDate })
    }
  }, [onMouseMove, updateTask, bulkShiftDates])

  const startDrag = (task: GanttTask, mode: "move" | "resize") => (e: React.MouseEvent) => {
    e.stopPropagation()
    didDragRef.current = false
    const multi = mode === "move" && selectMode && checkedIds.has(task.id) && checkedIds.size > 1
    const ids = multi ? Array.from(checkedIds) : [task.id]
    const orig = new Map<string, { start: Date; end: Date }>()
    for (const id of ids) {
      const t = tasksById.get(id)
      if (t) orig.set(id, { start: toDate(t.startDate), end: toDate(t.endDate) })
    }
    dragRef.current = { ids, mode, startX: e.clientX, orig }
    ;(window as any).__ganttDragDelta = 0
    window.addEventListener("mousemove", onMouseMove)
    window.addEventListener("mouseup", onMouseUp)
  }

  const handleBarClick = (task: GanttTask) => {
    if (didDragRef.current) {
      didDragRef.current = false
      return
    }
    if (selectMode) toggleChecked(task.id)
    else setSelection({ type: "task", id: task.id })
  }

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", onMouseMove)
      window.removeEventListener("mouseup", onMouseUp)
    }
  }, [onMouseMove, onMouseUp])

  useEffect(() => {
    if (loading) return
    const x = xForDateStr(TODAY_STR)
    scrollRef.current?.scrollTo({ left: Math.max(0, x - 260) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  const scrollToToday = () => scrollRef.current?.scrollTo({ left: Math.max(0, xForDateStr(TODAY_STR) - 260), behavior: "smooth" })
  const nudge = (dir: 1 | -1) => scrollRef.current?.scrollBy({ left: dir * 320, behavior: "smooth" })
  const togglePhase = (phaseId: string) => {
    setCollapsedPhases((prev) => {
      const next = new Set(prev)
      next.has(phaseId) ? next.delete(phaseId) : next.add(phaseId)
      return next
    })
  }

  const toggleChecked = (id: string) => {
    setCheckedIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  // Checking a Phase's checkbox selects/deselects every Task belonging to
  // it (spec #3) -- computed from childrenByPhase, not stored separately.
  const togglePhaseChecked = (phaseId: string) => {
    const childIds = (childrenByPhase.get(phaseId) || []).map((t) => t.id)
    if (childIds.length === 0) return
    const allChecked = childIds.every((id) => checkedIds.has(id))
    setCheckedIds((prev) => {
      const next = new Set(prev)
      childIds.forEach((id) => (allChecked ? next.delete(id) : next.add(id)))
      return next
    })
  }

  // ---- row drag-and-drop reorder -----------------------------------------------
  const onRowDragStart = (kind: "phase" | "task", id: string) => (e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = "move"
    rowDragRef.current = { kind, id }
  }
  const onRowDragOver = (e: React.DragEvent) => e.preventDefault()
  const onPhaseRowDrop = (phase: GanttTask) => (e: React.DragEvent) => {
    e.preventDefault()
    const drag = rowDragRef.current
    rowDragRef.current = null
    if (!drag) return
    if (drag.kind === "phase") reorderSiblings("phase", drag.id, phase.id, null)
    else reorderSiblings("task", drag.id, "__end__", phase.id)
  }
  const onTaskRowDrop = (task: GanttTask, phaseId: string) => (e: React.DragEvent) => {
    e.preventDefault()
    const drag = rowDragRef.current
    rowDragRef.current = null
    if (!drag || drag.kind !== "task") return
    reorderSiblings("task", drag.id, task.id, phaseId)
  }

  if (loading) {
    return (
      <div className="flex h-full min-h-0 flex-1 items-center justify-center rounded-xl border border-gray-200 bg-white">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    )
  }

  if (!projectId) {
    return (
      <div className="flex h-full min-h-0 flex-1 items-center justify-center rounded-xl border border-gray-200 bg-white text-sm text-gray-400">
        Select a project to view its schedule.
      </div>
    )
  }

  return (
    <div className="relative flex h-full min-h-0 flex-col rounded-xl border border-gray-200 bg-white overflow-hidden">
      {/* Toolbar -- Add Phase/Add Task live here (left, above the list),
          matching Gantt's original layout. Select mode/bulk actions and
          Search/3-dot live in scheduling-page.tsx's CardHeader. */}
      <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-4 py-2.5 bg-gray-50/60">
        <div className="flex flex-wrap items-center gap-2">
          <StandardButton customVariant="white" onClick={() => addPhase()} className="h-9 px-3 text-xs">
            <Layers className="mr-1.5 h-3.5 w-3.5" />
            Add Phase
          </StandardButton>
          <AddTaskButton phases={phases} onAddTask={addTask} className="h-9 px-3 text-xs" />
          <div className="mx-1 h-5 w-px bg-gray-200" />
          <ToggleWhiteButton active={lookahead} onClick={() => setLookahead((v) => !v)}>
            {lookahead && <Check className="mr-1.5 h-3.5 w-3.5" />}
            6 Week Lookahead
          </ToggleWhiteButton>
          <div className="mx-1 h-5 w-px bg-gray-200" />
          <StandardButton customVariant="white" onClick={() => nudge(-1)} className="h-9 w-9 p-0">
            <ChevronLeft className="h-4 w-4" />
          </StandardButton>
          <StandardButton customVariant="blue" onClick={scrollToToday} className="h-9 px-3 text-xs">
            <Crosshair className="mr-1.5 h-3.5 w-3.5" />
            Today
          </StandardButton>
          <StandardButton customVariant="white" onClick={() => nudge(1)} className="h-9 w-9 p-0">
            <ChevronRight className="h-4 w-4" />
          </StandardButton>
        </div>

        <div className="flex items-center gap-2">
          <GanttWeatherBadge projectId={projectId} companyId={companyId} />
          <div className="flex items-center gap-0.5 rounded-lg border border-gray-200 bg-white p-0.5">
            {(["day", "week", "month", "quarter"] as Zoom[]).map((z) => (
              <ZoomButton key={z} active={zoom === z} onClick={() => setZoom(z)}>
                {z}
              </ZoomButton>
            ))}
          </div>
        </div>
      </div>

      {phases.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 text-sm text-gray-400">
          <p>This project has no schedule yet.</p>
          <StandardButton customVariant="blue" onClick={() => addPhase()} className="text-xs">
            <Layers className="mr-1.5 h-3.5 w-3.5" />
            Add the first Phase
          </StandardButton>
        </div>
      ) : (
        <div ref={scrollRef} className="relative min-h-0 flex-1 overflow-auto">
          <div style={{ width: LIST_WIDTH + timelineWidth, position: "relative" }}>
            {/* Header row */}
            <div className="sticky top-0 z-30 flex bg-white" style={{ height: HEADER_HEIGHT }}>
              <div
                className="sticky left-0 z-40 flex items-end border-b border-r border-gray-200 bg-white px-3 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-400"
                style={{ width: LIST_WIDTH, flexShrink: 0 }}
              >
                Phase / Task
              </div>
              <div className="relative border-b border-gray-200" style={{ width: timelineWidth, flexShrink: 0 }}>
                <div className="relative h-[24px]">
                  {monthTicks.map((m) => (
                    <div key={m.label + m.left} className="absolute top-0 flex h-full items-center border-l border-gray-100 pl-2 text-xs font-semibold text-gray-600" style={{ left: m.left, width: m.width }}>
                      {m.label}
                    </div>
                  ))}
                </div>
                <div className="relative h-[28px]">
                  {dayTicks.map((d, i) => (
                    <div
                      key={d.left}
                      className={cn(
                        "absolute top-0 flex h-full flex-col items-center justify-center overflow-hidden border-l border-gray-200 text-[11px] leading-none",
                        d.weekend ? "text-gray-400" : "text-gray-600"
                      )}
                      style={{ left: d.left, width: pxPerDay }}
                    >
                      {i % dayLabelStep === 0 && (
                        <>
                          <span className="font-medium">{format(d.date, "d")}</span>
                          <span className="text-[10px] text-gray-400">{format(d.date, "EEEEE")}</span>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Rows */}
            {rows.map((row) => {
              if (row.kind === "phase") {
                const phase = row.task
                const color = phaseColor(phase.id)
                const collapsed = collapsedPhases.has(phase.id)
                const isSelected = selectedPhase?.id === phase.id
                const childIds = (childrenByPhase.get(phase.id) || []).map((t) => t.id)
                const isChecked = childIds.length > 0 && childIds.every((id) => checkedIds.has(id))
                const summary = phaseSummaries.get(phase.id)!
                const left = xForDateStr(summary.start)
                const width = Math.max(pxPerDay, (daysBetween(toDate(summary.start), toDate(summary.end)) + 1) * pxPerDay - 3)

                return (
                  <div key={phase.id} className={cn("flex bg-gray-100/80", (isSelected || isChecked) && "bg-orange-50/50")} style={{ height: ROW_HEIGHT }}>
                    <div
                      draggable={!selectMode}
                      onDragStart={onRowDragStart("phase", phase.id)}
                      onDragOver={onRowDragOver}
                      onDrop={onPhaseRowDrop(phase)}
                      onClick={() => (selectMode ? togglePhaseChecked(phase.id) : setSelection({ type: "phase", id: phase.id }))}
                      className={cn("sticky left-0 z-20 flex cursor-pointer items-center gap-1 border-r border-b border-gray-200 bg-gray-100/80 px-2 transition-colors hover:bg-gray-200/70", (isSelected || isChecked) && "bg-orange-50/50 hover:bg-orange-50/50")}
                      style={{ width: LIST_WIDTH, flexShrink: 0 }}
                    >
                      {selectMode ? (
                        <Checkbox checked={isChecked} onCheckedChange={() => togglePhaseChecked(phase.id)} onClick={(e) => e.stopPropagation()} className="h-3.5 w-3.5 flex-shrink-0" />
                      ) : (
                        <GripVertical className="h-3 w-3 flex-shrink-0 cursor-grab text-gray-400" />
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          togglePhase(phase.id)
                        }}
                        className="flex-shrink-0 rounded p-0.5 text-gray-400 hover:text-gray-600"
                        {...tertiaryHover}
                      >
                        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", collapsed && "-rotate-90")} />
                      </button>
                      <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ backgroundColor: color }} />
                      <span className="truncate text-[13px] font-semibold text-gray-700">{phase.name}</span>
                      <span className="flex-shrink-0 text-xs text-gray-400">({row.count})</span>
                    </div>
                    <div className="relative cursor-pointer border-b border-gray-200" style={{ width: timelineWidth, flexShrink: 0 }} onClick={() => (selectMode ? togglePhaseChecked(phase.id) : setSelection({ type: "phase", id: phase.id }))}>
                      <WeekendStrips dayTicks={dayTicks} pxPerDay={pxPerDay} />
                      <div className="absolute top-1/2 -translate-y-1/2 rounded-md" style={{ left, width, height: 14, backgroundColor: `${color}1f`, border: `1.5px ${isSelected ? "solid" : "dashed"} ${color}aa` }}>
                        <div className="h-full rounded-md" style={{ width: `${summary.progress}%`, backgroundColor: `${color}55` }} />
                      </div>
                    </div>
                  </div>
                )
              }

              const task = row.task
              const isSelected = selectedTask?.id === task.id
              const isChecked = checkedIds.has(task.id)
              const dim = relatedIds ? !relatedIds.has(task.id) : false
              const left = xForDateStr(task.startDate)
              const width = Math.max(pxPerDay * 0.6, (daysBetween(toDate(task.startDate), toDate(task.endDate)) + 1) * pxPerDay - 3)
              const color = phaseColor(row.phaseId)
              const hasDeps = dependencies.some((d) => d.fromTaskId === task.id || d.toTaskId === task.id)
              const health = getScheduleHealthFlag(task)

              return (
                <div key={task.id} className={cn("flex transition-colors", (isSelected || isChecked) && "bg-orange-50/60")} style={{ height: ROW_HEIGHT }}>
                  <div
                    draggable={!selectMode}
                    onDragStart={onRowDragStart("task", task.id)}
                    onDragOver={onRowDragOver}
                    onDrop={onTaskRowDrop(task, row.phaseId)}
                    onClick={() => (selectMode ? toggleChecked(task.id) : setSelection({ type: "task", id: task.id }))}
                    className={cn("sticky left-0 z-20 flex cursor-pointer items-center gap-1.5 border-r border-b border-gray-200 bg-white px-3 pl-6 text-[13.5px] text-gray-700 transition-colors hover:bg-gray-50", (isSelected || isChecked) && "bg-orange-50/60 hover:bg-orange-50/60")}
                    style={{ width: LIST_WIDTH, flexShrink: 0 }}
                  >
                    {selectMode ? (
                      <Checkbox checked={isChecked} onCheckedChange={() => toggleChecked(task.id)} onClick={(e) => e.stopPropagation()} className="h-3.5 w-3.5 flex-shrink-0" />
                    ) : (
                      <GripVertical className="h-3 w-3 flex-shrink-0 cursor-grab text-gray-400" />
                    )}
                    <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full" style={{ backgroundColor: STATUS_DOT_COLOR[task.status] }} title={STATUS_LABEL[task.status]} />
                    <span className="truncate">{task.name}</span>
                    {hasDeps && <Link2 className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                    {health && (
                      <span title={SCHEDULE_HEALTH_LABEL[health]}>
                        <AlertTriangle className="h-3 w-3 flex-shrink-0 text-red-500" />
                      </span>
                    )}
                  </div>
                  <div className="relative border-b border-gray-200" style={{ width: timelineWidth, flexShrink: 0 }} onClick={() => handleBarClick(task)}>
                    <WeekendStrips dayTicks={dayTicks} pxPerDay={pxPerDay} />
                    <div
                      data-testid={`gantt-bar-${task.id}`}
                      onMouseDown={startDrag(task, "move")}
                      className={cn("group absolute top-1/2 -translate-y-1/2 flex cursor-grab items-center rounded-md transition-opacity active:cursor-grabbing", dim ? "opacity-25" : "opacity-100")}
                      style={{ left, width, height: 20, backgroundColor: `${color}22`, border: `1.5px solid ${isSelected || isChecked ? color : `${color}88`}`, boxShadow: isSelected ? `0 0 0 3px ${color}22` : undefined }}
                    >
                      <div className="h-full rounded-l-[5px]" style={{ width: `${task.progress}%`, backgroundColor: `${color}55` }} />
                      <span className="pointer-events-none absolute left-2 whitespace-nowrap text-xs font-medium" style={{ color }}>
                        {width > 70 ? task.name : ""}
                      </span>
                      <div data-testid={`gantt-resize-${task.id}`} onMouseDown={startDrag(task, "resize")} className="absolute -right-1.5 top-0 z-10 h-full w-3 cursor-ew-resize opacity-0 group-hover:opacity-100">
                        <div className="mx-auto h-full w-[3px] rounded-full bg-gray-900/40" />
                      </div>
                    </div>
                    {dragPreview && dragPreview.taskId === task.id && (() => {
                      const ghostLeft = xForDateStr(dragPreview.startDate)
                      const ghostWidth = Math.max(pxPerDay * 0.6, (daysBetween(toDate(dragPreview.startDate), toDate(dragPreview.endDate)) + 1) * pxPerDay - 3)
                      return (
                        <div
                          className="pointer-events-none absolute top-1/2 -translate-y-1/2 rounded-md border-2 border-dashed"
                          style={{ left: ghostLeft, width: ghostWidth, height: 20, backgroundColor: `${color}33`, borderColor: color, opacity: 0.75 }}
                        />
                      )
                    })()}
                  </div>
                </div>
              )
            })}

            {/* Dependency arrows overlay */}
            <svg className="pointer-events-none absolute top-0" style={{ left: LIST_WIDTH, top: HEADER_HEIGHT, width: timelineWidth, height: rows.length * ROW_HEIGHT }}>
              <defs>
                <marker id="gantt-arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                  <path d="M0,0 L6,3 L0,6 Z" fill="#94a3b8" />
                </marker>
                <marker id="gantt-arrow-active" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                  <path d="M0,0 L6,3 L0,6 Z" fill={colors.buttonPrimary} />
                </marker>
              </defs>
              {dependencies.map((dep) => {
                const from = tasksById.get(dep.fromTaskId)
                const to = tasksById.get(dep.toTaskId)
                const toIdx = rowIndexByTaskId.get(dep.toTaskId)
                const fromIdx = rowIndexByTaskId.get(dep.fromTaskId)
                if (!from || !to || toIdx === undefined || fromIdx === undefined) return null

                const ax = xForDateStr(from.endDate) + pxPerDay - 2
                const ayRel = fromIdx * ROW_HEIGHT + ROW_HEIGHT / 2
                const bx = xForDateStr(to.startDate)
                const byRel = toIdx * ROW_HEIGHT + ROW_HEIGHT / 2
                const midX = ax + 10

                const active = !!selectedTask && (selectedTask.id === dep.fromTaskId || selectedTask.id === dep.toTaskId)
                const strokeColor = active ? colors.buttonPrimary : "#cbd5e1"

                return (
                  <path
                    key={dep.id}
                    d={`M ${ax} ${ayRel} L ${midX} ${ayRel} L ${midX} ${byRel} L ${bx - 3} ${byRel}`}
                    fill="none"
                    stroke={strokeColor}
                    strokeWidth={active ? 2 : 1.5}
                    markerEnd={active ? "url(#gantt-arrow-active)" : "url(#gantt-arrow)"}
                    opacity={relatedIds && !active ? 0.25 : 1}
                  />
                )
              })}

              {(() => {
                const x = xForDateStr(TODAY_STR)
                if (x < 0 || x > timelineWidth) return null
                return <line x1={x} x2={x} y1={0} y2={rows.length * ROW_HEIGHT} stroke={colors.buttonPrimary} strokeWidth={1.5} strokeDasharray="4 3" />
              })()}
            </svg>
            {(() => {
              const x = xForDateStr(TODAY_STR)
              if (x < 0 || x > timelineWidth) return null
              return (
                <div className="pointer-events-none absolute z-20 rounded-full px-1.5 py-0.5 text-[10px] font-semibold text-white" style={{ left: LIST_WIDTH + x - 12, top: HEADER_HEIGHT - 18, backgroundColor: colors.buttonPrimary }}>
                  Today
                </div>
              )
            })()}
          </div>
        </div>
      )}

      {selectedTask && (
        <GanttTaskPanel
          task={selectedTask}
          ganttData={ganttData}
          companyId={companyId}
          userId={userId}
          projectId={projectId}
          onClose={() => setSelection(null)}
          onSelectTask={(id) => setSelection({ type: "task", id })}
        />
      )}
      {selectedPhase && !selectedTask && (
        <PhaseDrawer
          phase={selectedPhase}
          summary={phaseSummaries.get(selectedPhase.id) ?? { start: selectedPhase.startDate, end: selectedPhase.endDate, progress: selectedPhase.progress, count: 0 }}
          accentColor={phaseColor(selectedPhase.id)}
          collapsed={collapsedPhases.has(selectedPhase.id)}
          onToggleCollapsed={() => togglePhase(selectedPhase.id)}
          onClose={() => setSelection(null)}
          onRename={(newName) => renamePhase(selectedPhase.id, newName)}
          onDelete={() => deletePhase(selectedPhase.id)}
        />
      )}
    </div>
  )
}

/**
 * A task's details / edit panel (the TaskDrawer) wired to the schedule data: its dependencies, phase, color and every edit
 * (update, delete, dependencies, baseline). The ONE place a task opens from -- the Gantt timeline and the List view both render
 * this, so a task is viewed and edited the same way in both.
 */
export function GanttTaskPanel({
  task,
  ganttData,
  companyId,
  userId,
  projectId,
  onClose,
  onSelectTask,
}: {
  task: GanttTask
  ganttData: UseGanttData
  companyId?: string | null
  userId?: string
  projectId?: string
  onClose: () => void
  onSelectTask: (id: string) => void
}) {
  const { tasks, dependencies, phases, phaseColor, updateTask, deleteTask, addDependency, updateDependency, removeDependency, setBaseline } = ganttData
  const [cycleAlert, setCycleAlert] = useState(false)
  const incomingDeps = useMemo(() => dependencies.filter((d) => d.toTaskId === task.id), [dependencies, task.id])
  return (
    <>
      <TaskDrawer
        task={task}
        allTasks={tasks}
        incomingDeps={incomingDeps}
        phaseName={phases.find((p) => p.id === task.parentId)?.name ?? ""}
        accentColor={task.parentId ? phaseColor(task.parentId) : "#64748b"}
        companyId={companyId}
        userId={userId}
        projectId={projectId}
        onClose={onClose}
        onUpdate={(patch) => updateTask(task.id, patch)}
        onDelete={() => deleteTask(task.id)}
        onRemoveDependency={removeDependency}
        onUpdateDependency={updateDependency}
        onAddDependency={(fromId, type, lag) => {
          if (!addDependency(fromId, task.id, type, lag)) setCycleAlert(true)
        }}
        onSelectTask={onSelectTask}
        onSetBaseline={() => setBaseline([task.id])}
      />
      {cycleAlert && (
        <FloatingAlert
          variant="destructive"
          title="Can't add that dependency"
          description="This would create a circular dependency (A → B → ... → A), which the schedule engine can't resolve."
          onDismiss={() => setCycleAlert(false)}
        />
      )}
    </>
  )
}

// Weekend column shading, shared by Phase and Task rows alike (previously
// only rendered inside Task rows) -- rendered as the FIRST child of each
// row's own `position:relative` timeline container so it paints behind
// that row's bar, using the exact same `dayTicks`/`pxPerDay` the header
// ticks are built from, so the columns always line up.
function WeekendStrips({ dayTicks, pxPerDay }: { dayTicks: { left: number; weekend: boolean }[]; pxPerDay: number }) {
  return (
    <>
      {dayTicks
        .filter((d) => d.weekend)
        .map((d) => <div key={d.left} className="pointer-events-none absolute top-0 h-full" style={{ left: d.left, width: pxPerDay, backgroundColor: colors.scheduleWeekendBg }} />)}
    </>
  )
}

// A generic-white toggle button (StandardButton's "white" variant, same
// hover) that also reflects an on/off state via a checkmark instead of a
// bespoke active color (spec #1: "6 Week Lookahead ... debe usar el
// estilo genérico blanco y su hover genérico correspondiente").
function ToggleWhiteButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  const [hover, setHover] = useState(false)
  // StandardButton's own hover handlers are imperative (they write
  // e.currentTarget.style directly) and would stomp over an "active" fill
  // the moment the mouse left -- passing our own onMouseEnter/onMouseLeave
  // here overrides those (same override-by-spread-order as ZoomButton
  // above). Active state uses KrewUp's real primary blue
  // (colors.buttonSecondary -- see GenericTabBar's header comment for why
  // it's not buttonPrimary/orange), same as Today/the active Day-Week-
  // Month segment, so it's unmistakably "on".
  return (
    <StandardButton
      customVariant="white"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={cn("h-9 px-3 text-xs", active ? "border-transparent text-white" : "text-gray-700")}
      style={{ backgroundColor: active ? colors.buttonSecondary : hover ? colors.buttonTertiaryHover : colors.buttonTertiary }}
    >
      {children}
    </StandardButton>
  )
}

// Day/Week/Month/Quarter segmented control: active segment uses KrewUp's
// real primary (design-system/colors.ts's buttonSecondary, blue -- see
// GenericTabBar's header comment for why it's not buttonPrimary/orange),
// same as the top-level view tabs; inactive segments use the exact same
// hover technique StandardButton's own "white" variant uses
// (buttonTertiaryHover), not a hardcoded Tailwind gray.
function ZoomButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  const [hover, setHover] = useState(false)
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={cn("rounded-md px-3 py-1 text-sm font-medium capitalize transition-colors", active ? "text-white" : "text-gray-500")}
      style={{ backgroundColor: active ? colors.buttonSecondary : hover ? colors.buttonTertiaryHover : "transparent" }}
    >
      {children}
    </button>
  )
}

function DrawerShell({ accentColor, kicker, onClose, children }: { accentColor: string; kicker: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="absolute z-50 flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl" style={{ top: 64, right: 12, bottom: 12, width: "min(360px, calc(100% - 24px))" }}>
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3" style={{ backgroundColor: `${accentColor}0d` }}>
        <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: accentColor }}>
          {kicker}
        </span>
        <button onClick={onClose} className="generic-close-button">
          <X className="generic-close-button-icon" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4">{children}</div>
    </div>
  )
}

function TaskDrawer({
  task,
  allTasks,
  incomingDeps,
  phaseName,
  accentColor,
  companyId,
  userId,
  projectId,
  onClose,
  onUpdate,
  onDelete,
  onRemoveDependency,
  onUpdateDependency,
  onAddDependency,
  onSelectTask,
  onSetBaseline,
}: {
  task: GanttTask
  allTasks: GanttTask[]
  incomingDeps: GanttDependency[]
  phaseName: string
  accentColor: string
  companyId?: string | null
  userId?: string
  /** The project the schedule belongs to: where Browse Drive looks. */
  projectId?: string
  onClose: () => void
  onUpdate: (patch: Partial<GanttTask>) => void
  onDelete: () => void
  onRemoveDependency: (dep: GanttDependency) => void
  onUpdateDependency: (dep: GanttDependency, patch: { type?: GanttDependency["type"]; lagDays?: number }) => void
  onAddDependency: (fromId: string, type: GanttDependency["type"], lag: number) => void
  onSelectTask: (id: string) => void
  onSetBaseline: () => void
}) {
  const start = toDate(task.startDate)
  const end = toDate(task.endDate)
  // task.duration is WORKING days (schedule-date-engine.ts) -- NOT
  // differenceInCalendarDays, which would count weekends/non-working days.
  const duration = task.duration
  const color = accentColor
  const [depType, setDepType] = useState<GanttDependency["type"]>("finish-to-start")
  const [depLag, setDepLag] = useState(0)
  const [tab, setTab] = useState<"details" | "comments" | "attachments">("details")
  const members = useCompanyMembers(companyId)
  const { ensureMembers, prompt: membershipPrompt } = useProjectMembershipPrompt()
  // A Gantt edit is saved the moment it is made, so the shared "add to project?" popup is asked right then; declining leaves the task unassigned
  const changeAssignee = async (personId: string) => {
    if (personId && projectId && !(await ensureMembers(projectId, [{ id: personId, name: members.find((m) => m.id === personId)?.fullName || "This person" }]))) {
      onUpdate({ assigneeId: null })
      return
    }
    onUpdate({ assigneeId: personId || null })
  }
  const { comments, addComment, deleteComment } = useEntityComments("schedule_task", tab === "comments" ? task.id : null)
  const [attachments, setAttachments] = useState<ScheduleFileAttachment[]>([])

  useEffect(() => {
    if (tab === "attachments") fetchScheduleTaskAttachments(task.id).then(setAttachments)
  }, [tab, task.id])

  const health = getScheduleHealthFlag(task)
  const depOptions = allTasks.filter((t) => t.type === "task" && t.id !== task.id && !incomingDeps.some((d) => d.fromTaskId === t.id))

  const handleUpload = async (files: File[]) => {
    if (!companyId || !userId) return
    const uploaded = await uploadScheduleTaskAttachments(task.id, files, companyId, userId)
    setAttachments((prev) => [...prev, ...uploaded])
  }
  const handleDeleteAttachment = async (attachmentId: string) => {
    await deleteScheduleTaskAttachment(attachmentId, userId)
    setAttachments((prev) => prev.filter((a) => a.id !== attachmentId))
  }

  return (
    <DrawerShell accentColor={color} kicker={`Task · ${phaseName}`} onClose={onClose}>
      {membershipPrompt}
      <div className="mb-3 flex gap-1 border-b border-gray-100 pb-2 text-xs font-medium">
        {(["details", "attachments", "comments"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn("rounded-md px-2.5 py-1 capitalize transition-colors", tab === t ? "text-white" : "text-gray-500")}
            style={tab === t ? { backgroundColor: colors.buttonSecondary } : undefined}
            {...(tab === t ? {} : tertiaryHover)}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "details" && (
        <>
          {health && (
            <div className="mb-3 flex items-center gap-1.5 rounded-md border border-red-100 bg-red-50 px-2.5 py-1.5 text-xs text-red-600">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
              {SCHEDULE_HEALTH_LABEL[health]}
            </div>
          )}
          <input value={task.name} onChange={(e) => onUpdate({ name: e.target.value })} className="mb-3 w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-sm font-semibold text-gray-900 focus:border-gray-400 focus:outline-none" />

          <div className="mb-3">
            <label className="mb-1 block text-xs font-medium text-gray-400">Status</label>
            <select value={task.status} onChange={(e) => onUpdate({ status: e.target.value as GanttTask["status"] })} className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm text-gray-700 focus:border-gray-400 focus:outline-none">
              {(Object.keys(STATUS_LABEL) as GanttTask["status"][]).map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-3">
            <label className="mb-1 block text-xs font-medium text-gray-400">Assignee</label>
            <select value={task.assigneeId ?? ""} onChange={(e) => void changeAssignee(e.target.value)} className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm text-gray-700 focus:border-gray-400 focus:outline-none">
              <option value="">Select</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.fullName}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-3 grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-400">Start</label>
              <input
                type="date"
                value={task.startDate}
                // Only startDate is passed -- updateTask (useGanttData)
                // re-derives endDate from the task's EXISTING working-day
                // duration, so moving the start never shrinks/grows how
                // many working days the task spans.
                onChange={(e) => onUpdate({ startDate: e.target.value })}
                className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm text-gray-700 focus:border-gray-400 focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-400">Duration (working days)</label>
              <input
                type="number"
                min={1}
                value={duration}
                // Only duration is passed -- updateTask re-derives endDate
                // from it via the working-days engine.
                onChange={(e) => onUpdate({ duration: Math.max(1, parseInt(e.target.value) || 1) })}
                className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm text-gray-700 focus:border-gray-400 focus:outline-none"
              />
            </div>
          </div>

          <div className="mb-3 flex items-center justify-between rounded-md border border-gray-100 bg-gray-50 px-2.5 py-2 text-xs">
            <div>
              <span className="text-gray-400">Baseline: </span>
              {task.baselineStartDate ? (
                <span className="font-medium text-gray-700">
                  {format(toDate(task.baselineStartDate), "MMM d")} → {format(toDate(task.baselineEndDate!), "MMM d, yyyy")}
                </span>
              ) : (
                <span className="text-gray-400">Not set</span>
              )}
            </div>
            <StandardButton customVariant="white" onClick={onSetBaseline} className="h-7 flex-shrink-0 px-2 text-[11.5px]">
              Set Baseline
            </StandardButton>
          </div>

          <div className="mb-3">
            <label className="mb-1 block text-xs font-medium text-gray-400">Description</label>
            <Textarea
              compact
              value={task.description ?? ""}
              onChange={(e) => onUpdate({ description: e.target.value })}
              rows={2}
              placeholder="Add a short description…"
            />
          </div>

          <div className="mb-4">
            <label className="mb-1 flex items-center justify-between text-xs font-medium text-gray-400">
              <span>Progress</span>
              <span className="text-gray-600">{task.progress}%</span>
            </label>
            <input type="range" min={0} max={100} value={task.progress} onChange={(e) => onUpdate({ progress: parseInt(e.target.value) })} className="w-full accent-gray-900" />
          </div>

          <div className="mb-4">
            <label className="mb-1.5 block text-xs font-medium text-gray-400">Depends on</label>
            {incomingDeps.length === 0 && <p className="text-xs text-gray-300">No dependencies yet</p>}
            <div className="flex flex-col gap-2">
              {incomingDeps.map((dep) => {
                const depTask = allTasks.find((t) => t.id === dep.fromTaskId)
                if (!depTask) return null
                return (
                  <div key={dep.id} className="rounded-md border border-gray-200 bg-gray-50 px-2.5 py-2">
                    <div className="mb-1.5 flex items-center justify-between gap-2">
                      <button onClick={() => onSelectTask(dep.fromTaskId)} className="min-w-0 truncate text-left text-[12.5px] font-medium text-gray-700 hover:text-gray-900">
                        {depTask.name}
                      </button>
                      <button onClick={() => onRemoveDependency(dep)} className="flex-shrink-0 text-gray-400 hover:text-red-500" title="Remove dependency">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="flex items-end gap-1.5">
                      <div className="flex-1">
                        <label className="mb-0.5 block text-[10px] font-medium uppercase tracking-wide text-gray-400">Type</label>
                        <select
                          value={dep.type}
                          onChange={(e) => onUpdateDependency(dep, { type: e.target.value as GanttDependency["type"] })}
                          className="w-full rounded-md border border-gray-200 bg-white px-1.5 py-1 text-[11.5px] text-gray-700 focus:border-gray-400 focus:outline-none"
                        >
                          {(Object.keys(DEP_TYPE_LABEL) as GanttDependency["type"][]).map((t) => (
                            <option key={t} value={t}>
                              {DEP_TYPE_LABEL[t]}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="w-20 flex-shrink-0">
                        <label className="mb-0.5 block text-[10px] font-medium uppercase tracking-wide text-gray-400">Lag (days)</label>
                        <input
                          type="number"
                          value={dep.lagDays ?? 0}
                          onChange={(e) => onUpdateDependency(dep, { lagDays: parseInt(e.target.value) || 0 })}
                          className="w-full rounded-md border border-gray-200 bg-white px-1.5 py-1 text-[11.5px] text-gray-700 focus:border-gray-400 focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
            {depOptions.length > 0 && (
              <div className="mt-2.5 rounded-md border border-dashed border-gray-200 p-2.5">
                <p className="mb-1.5 text-[11px] font-medium text-gray-500">Add a dependency</p>
                <div className="mb-1.5 flex items-end gap-1.5">
                  <div className="flex-1">
                    <label className="mb-0.5 block text-[10px] font-medium uppercase tracking-wide text-gray-400">Type</label>
                    <select value={depType} onChange={(e) => setDepType(e.target.value as GanttDependency["type"])} className="w-full rounded-md border border-gray-200 bg-white px-1.5 py-1 text-[11.5px] text-gray-700 focus:border-gray-400 focus:outline-none">
                      {(Object.keys(DEP_TYPE_LABEL) as GanttDependency["type"][]).map((t) => (
                        <option key={t} value={t}>
                          {DEP_TYPE_LABEL[t]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="w-20 flex-shrink-0">
                    <label className="mb-0.5 block text-[10px] font-medium uppercase tracking-wide text-gray-400">Lag (days)</label>
                    <input type="number" value={depLag} onChange={(e) => setDepLag(parseInt(e.target.value) || 0)} className="w-full rounded-md border border-gray-200 bg-white px-1.5 py-1 text-[11.5px] text-gray-700 focus:border-gray-400 focus:outline-none" />
                  </div>
                </div>
                <p className="mb-1.5 text-[10px] leading-snug text-gray-400">Working days to wait after this dependency is satisfied. 0 = as soon as possible, negative = an allowed overlap.</p>
                <select
                  defaultValue=""
                  onChange={(e) => {
                    onAddDependency(e.target.value, depType, depLag)
                    e.currentTarget.value = ""
                  }}
                  className="w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 text-[12.5px] text-gray-500 focus:border-gray-400 focus:outline-none"
                >
                  <option value="" disabled>
                    + Choose predecessor task
                  </option>
                  {depOptions.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <SaveChangesButton type="button" onClick={onClose} className="h-9 flex-1 px-3 text-sm">
              Done
            </SaveChangesButton>
            <DeleteRecordButton
              label="Delete Task"
              className="h-9 flex-1"
              onClick={() => {
                if (window.confirm("Are you sure you want to delete this task? This action cannot be undone.")) onDelete()
              }}
            />
          </div>
        </>
      )}

      {tab === "comments" && (
        <GenericCommentsSection comments={comments} onAddComment={addComment} onDeleteComment={deleteComment} mode="edit" audiences={TEAM_CLIENT_VENDOR_AUDIENCES} />
      )}

      {tab === "attachments" && (
        <GenericAttachmentsSection attachments={attachments} onUpload={handleUpload} onDelete={handleDeleteAttachment} mode="edit" projectId={projectId ?? ""} />
      )}
    </DrawerShell>
  )
}

function PhaseDrawer({
  phase,
  summary,
  accentColor,
  collapsed,
  onToggleCollapsed,
  onClose,
  onRename,
  onDelete,
}: {
  phase: GanttTask
  summary: { start: string; end: string; progress: number; count: number }
  accentColor: string
  collapsed: boolean
  onToggleCollapsed: () => void
  onClose: () => void
  onRename: (newName: string) => void
  onDelete: () => void
}) {
  const color = accentColor
  const start = toDate(summary.start)
  const end = toDate(summary.end)
  const duration = differenceInCalendarDays(end, start) + 1
  const [draft, setDraft] = useState(phase.name)

  useEffect(() => setDraft(phase.name), [phase.name])

  return (
    <DrawerShell accentColor={color} kicker="Phase" onClose={onClose}>
      <div className="mb-4 flex items-center gap-2">
        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: `${color}18` }}>
          <Layers className="h-4 w-4" style={{ color }} />
        </span>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => onRename(draft)}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur()
          }}
          className="w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-sm font-semibold text-gray-900 focus:border-gray-400 focus:outline-none"
        />
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 text-xs">
        <div className="rounded-md border border-gray-100 bg-gray-50 p-2.5">
          <div className="text-gray-400">Start</div>
          <div className="mt-0.5 font-medium text-gray-700">{format(start, "MMM d, yyyy")}</div>
        </div>
        <div className="rounded-md border border-gray-100 bg-gray-50 p-2.5">
          <div className="text-gray-400">End</div>
          <div className="mt-0.5 font-medium text-gray-700">{format(end, "MMM d, yyyy")}</div>
        </div>
        <div className="rounded-md border border-gray-100 bg-gray-50 p-2.5">
          <div className="text-gray-400">Duration</div>
          <div className="mt-0.5 font-medium text-gray-700">{duration} days</div>
        </div>
        <div className="rounded-md border border-gray-100 bg-gray-50 p-2.5">
          <div className="text-gray-400">Tasks</div>
          <div className="mt-0.5 font-medium text-gray-700">{summary.count}</div>
        </div>
      </div>

      <div className="mb-4">
        <label className="mb-1 flex items-center justify-between text-xs font-medium text-gray-400">
          <span>Phase Progress</span>
          <span className="text-gray-600">{summary.progress}%</span>
        </label>
        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
          <div className="h-full rounded-full" style={{ width: `${summary.progress}%`, backgroundColor: color }} />
        </div>
        <p className="mt-1 text-[11.5px] text-gray-400">Weighted average of every task in this phase.</p>
      </div>

      <button onClick={onToggleCollapsed} className="mb-2 flex w-full items-center justify-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors" {...tertiaryHover}>
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", collapsed && "-rotate-90")} />
        {collapsed ? "Expand Tasks" : "Collapse Tasks"}
      </button>

      <DeleteRecordButton
        label="Delete Phase"
        className="h-9 w-full"
        onClick={() => {
          if (window.confirm("Are you sure you want to delete this phase? This action cannot be undone.")) onDelete()
        }}
      />
    </DrawerShell>
  )
}
